import { createContext, useContext, useState, useRef, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const ALLOWED_EXT = ["mp4", "mov", "webm", "avi", "mkv"];
const MAX_SIZE = 20 * 1024 * 1024 * 1024; // 20GB
const CHUNK_SIZE = 8 * 1024 * 1024; // 8MB
const MAX_RETRIES = 3;
const localKey = (gameId) => `gaffer_upload_${gameId}`;

const UploadContext = createContext(null);

// Lives at the app root (mounted once in App.js, never unmounted by route
// changes) so a video upload keeps running — and stays visible via the
// persistent nav indicator in Layout.jsx — no matter which page the coach
// is on. Losing this component would lose the upload; navigating between
// pages inside the app does not.
export function UploadProvider({ children }) {
  const [uploads, setUploads] = useState({}); // gameId -> { teamId, label, filename, status, sent, total, startedAt, error, uploadId }
  const filesRef = useRef({}); // gameId -> File (kept out of state; Files serialize fine but no need to trigger renders on assignment)
  const cancelRef = useRef({}); // gameId -> bool

  const patch = useCallback((gameId, patchObj) => {
    setUploads((prev) => {
      if (patchObj === null) {
        if (!(gameId in prev)) return prev;
        const next = { ...prev };
        delete next[gameId];
        return next;
      }
      return { ...prev, [gameId]: { ...prev[gameId], ...patchObj } };
    });
  }, []);

  const validate = (file) => {
    const ext = file.name.includes(".") ? file.name.split(".").pop().toLowerCase() : "";
    if (!ALLOWED_EXT.includes(ext)) return `Unsupported format ".${ext || "?"}". Supported: ${ALLOWED_EXT.join(", ")}.`;
    if (file.size > MAX_SIZE) return "File is too large. Max supported size is 20GB.";
    return null;
  };

  const runLoop = useCallback(async (teamId, gameId, label, file, id, totalChunks, chunkSize, received) => {
    cancelRef.current[gameId] = false;
    const startedAt = Date.now();
    let sent = received.length * chunkSize;
    patch(gameId, { teamId, label, filename: file.name, status: "uploading", sent, total: file.size, startedAt, error: null, uploadId: id });

    try {
      for (let i = 0; i < totalChunks; i++) {
        if (cancelRef.current[gameId]) return;
        if (received.includes(i)) continue;
        const start = i * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        const blob = file.slice(start, end);
        for (let attempt = 1; ; attempt++) {
          try {
            await api.put(`/video-upload/${id}/chunk/${i}`, blob, { headers: { "Content-Type": "application/octet-stream" } });
            break;
          } catch (err) {
            if (attempt >= MAX_RETRIES) throw err;
            await new Promise((r) => setTimeout(r, 1000 * attempt));
          }
        }
        if (cancelRef.current[gameId]) return;
        sent += end - start;
        patch(gameId, { sent });
      }
      patch(gameId, { status: "finalizing" });
      await api.post(`/video-upload/${id}/finalize`);
      localStorage.removeItem(localKey(gameId));
      delete filesRef.current[gameId];
      toast.success(`Video for ${label} finished uploading and is ready.`);
      patch(gameId, null);
    } catch (err) {
      if (cancelRef.current[gameId]) return;
      const msg = formatApiErrorDetail(err.response?.data?.detail) || "Upload interrupted — you can resume it anytime from the upload indicator.";
      patch(gameId, { status: "paused", error: msg });
      toast.error(`Upload for ${label} was interrupted.`);
    }
  }, [patch]);

  const startUpload = useCallback(async (teamId, gameId, label, file) => {
    const msg = validate(file);
    if (msg) { toast.error(msg); return; }
    filesRef.current[gameId] = file;
    try {
      let id, totalChunks, chunkSize, received = [];
      const saved = JSON.parse(localStorage.getItem(localKey(gameId)) || "null");
      if (saved && saved.filename === file.name && saved.totalSize === file.size) {
        try {
          const { data } = await api.get(`/video-upload/${saved.uploadId}/status`);
          if (data.status === "uploading") {
            id = data.id; totalChunks = data.total_chunks; chunkSize = data.chunk_size; received = data.received_chunks;
          }
        } catch { /* saved upload is gone server-side — start fresh below */ }
      }
      if (!id) {
        const { data } = await api.post(`/teams/${teamId}/games/${gameId}/video-upload/init`, {
          filename: file.name, content_type: file.type, total_size: file.size, chunk_size: CHUNK_SIZE,
        });
        id = data.id; totalChunks = data.total_chunks; chunkSize = data.chunk_size;
        localStorage.setItem(localKey(gameId), JSON.stringify({ uploadId: id, filename: file.name, totalSize: file.size }));
      }
      await runLoop(teamId, gameId, label, file, id, totalChunks, chunkSize, received);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  }, [runLoop]);

  const resumeUpload = useCallback((gameId) => {
    setUploads((prev) => {
      const entry = prev[gameId];
      const file = filesRef.current[gameId];
      if (entry && file) startUpload(entry.teamId, gameId, entry.label, file);
      return prev;
    });
  }, [startUpload]);

  const cancelUpload = useCallback((gameId) => {
    cancelRef.current[gameId] = true;
    delete filesRef.current[gameId];
    localStorage.removeItem(localKey(gameId));
    patch(gameId, null);
  }, [patch]);

  const getUpload = useCallback((gameId) => uploads[gameId] || null, [uploads]);

  return (
    <UploadContext.Provider value={{ uploads, startUpload, resumeUpload, cancelUpload, getUpload }}>
      {children}
    </UploadContext.Provider>
  );
}

export function useUpload() {
  return useContext(UploadContext);
}
